import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { Angulartics2RouterlessModule } from 'angulartics2';
import { bcStates } from 'common/common/states';
import { Observable, from } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment, config } from 'environments/environment';

// Modules
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { SvgIconModule } from './shared/components/svg-icon/svg-icon.module';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ngfModule } from 'angular-file';
import { VirtualScrollerModule } from 'ngx-virtual-scroller';
import { FormsModule } from '@angular/forms';
import { CoolStorageModule } from '@angular-cool/storage';
import { MatDialogModule } from '@angular/material/dialog';
import { NavbarModule } from './components/navbar/navbar.module';
import { EditorModule } from '@tinymce/tinymce-angular';
import { SvgAvatarModule } from './shared/components/svg-avatar/index';
import { NotificationBarModule } from './components/notification-bar/notification-bar.module';
import { LoginModule } from './components/login/login.module';
import { SvgSymbolsModule } from './components/svg-symbols/svg-symbols.module';
import { KeyboardShortcutsMenuModule } from 'components/keyboard-shortcuts-menu/keyboard-shortcuts-menu.module';
import { RouterModule } from '@angular/router';
import { RootLayoutModule } from 'components/root-layout/root.routes.module';
import { SocketIoModule } from 'ngx-socket-io';
import { VideoChatCallToastModule } from 'shared/components/video-chat-call-toast/video-chat-call-toast.module';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { GenesysCloudStoreModule } from 'common/store/genesys-cloud/genesys-cloud-store.module';
import { TelemetryModule } from 'common/telemetry/telemetry.module';
import { ErrorHandlerModule } from 'common/error-handler/error-handler.module';

// Components
import { CopyrightComponent } from './components/copyright/copyright.component';
import { LiveChatComponent } from './live-chat.component';
import { LoadingComponent } from './shared/components/loading/loading.component';
import { LoginComponent } from 'components/login/login.component';

// Services
import { IntegrationsService } from './common/integrations-service/integrations.service';
import { RemoteControlMessageService } from './common/remote-control/remote-control-message.service';
import { LaneManagerService } from './shared/components/lane-buttons/shared/services/lane-manager/lane-manager.service';
import { FileUploadService } from './common/api/file-upload-service';
import { UserPermissionService } from './shared/services/user-permissions/user-permission.service';
import { DesktopPopupService } from './common/desktop-popup-service/desktop-popup.service';
import { TranslationLoaderService } from './shared/services/translation-loader/translation-loader.service';
import { BrowserSupportService } from './shared/services/browser-support/browser-support.service';
import { ActiveAssistService } from './common/active-assist/active-assist.service';
import { WorkAreaManager } from './common/workitems/work-area-manager.service';
import { WorkItemStateHandler } from './shared/services/workitem-state-handler/workitem-state-handler.service';
import { TextChatMessagesService } from './common/text-chat-messages/text-chat-messages.service';
import { TextChatMessagesStoreFactoryService } from './common/text-chat-messages-store-factory/text-chat-messages-store-factory.service';
import { TextChatsStoreFactoryService } from './common/text-chats-store-factory/text-chats-store-factory.service';
import { TextChatsService } from './common/text-chats/text-chats.service';
import { TextChatUserStoreFactoryService } from './common/text-chat-user-store-factory/text-chat-user-store-factory.service';
import { TextChatUserService } from './common/text-chat-user/text-chat-user.service';
import { ServiceEvaluatorService } from './shared/services/service-evaluator/service-evaluator.service';
import { BrandingService } from './shared/services/branding';
import { WindowService } from './shared/services/window/window.service';
import { RxEmitter, RxEmitterInstance } from './common/rxjs/rx-emitter';
import { PinviteList } from './common/workitems/lists/pinvite-list';
import { EmailList } from './common/workitems/lists/email-list';
import { DiscussionList } from './common/workitems/lists/discussion-list';
import { ComposeEmailList } from './common/workitems/lists/compose-email-list';
import { LoginSubscriber } from './shared/services/subscribers/login-subscriber.service';
import { LogoutSubscriber } from './shared/services/subscribers/logout-subscriber.service';
import { BcAnalytics } from './common/analytics/analytics.service';
import { AudioService } from 'common/audio-service/audio.service';
import { AutoAnswersService } from './shared/components/chat-messages/auto-answers/auto-answers.service';
import { DynamicQueryAdapterService } from './common/dynamicquery/dynamic-query-adapter.service';
import { DynamicQueryDecoratorService } from './common/dynamicquery/dynamic-query-decorator.service';
import { TinyMCEPluginsService } from './shared/components/editor/tinymce-plugins.service';
import { WorkItemsTrackingService } from './common/workitems/workitems-tracking.service';
import { MessageGroupService } from './shared/services/message-group/message-group.service';
import { WorkItemNotificationsService } from './shared/services/workitem-notifications/workitem-notifications.service';
import { ActivityHandlerService } from './common/inactivity-handler/activity-handler.service';
import { NotificationBarService } from './shared/services/notification/notification-bar.service';
import { EmailAccountsService } from './components/work-item-lane-list/work-item-lane/work-item-widget/shared/services/email-accounts.service';
import { EmailSignatureInjectorService } from './components/work-item-lane-list/work-item-lane/work-item-widget/shared/services/email-signature-injector.service';
import { BcVisitorInfoBuilderService } from 'components/work-item-lane-list/work-item-lane/work-item-widget/shared/services/visitor-info-builder.service';
import { WorkItemObservableCacheService } from 'components/work-item-lane-list/work-item-lane/work-item-widget/shared/services/work-item-observable-cache.service';
import { BackendFieldConverterService } from './shared/services/backend-field-converter/backend-field-converter.service';
import { ChatsService } from 'common/chats/chats.service';
import { ChatsStoreFactoryService } from 'common/chats-store-factory/chats-store-factory.service';
import { StreamStatusService } from './shared/services/stream-status/stream-status.service';
import { LogoutResolverService } from 'common/common/states/resolvers/logout-state-resolver.service';
import { SSOStateResolverService } from 'common/common/states/resolvers/sso-state-resolver.service';
import { SsoErrorResolverService } from './common/common/states/resolvers/sso-error-state-resolver.service';
import { LoginIdentityGuard } from 'common/common/states/guards/login-identity-guard';
import { BcRestApi } from 'common/api/rest-api';
import { CsrfTokenService } from 'common/api/csrf-token/csrf-token.service';
import { CsrfSynchronizerTokenService } from 'common/api/csrf-token/csrf-synchronizer-token.service';
import { CsrfDoubleSubmitTokenService } from 'common/api/csrf-token/csrf-double-submit-token.service';
import { BcSettingsService } from 'common/settings/settings.service';
import { VisitorsService } from 'common/visitors/visitors.service';
import { BcHttpInterceptor } from 'common/api/http-interceptor';
import { BcConfigService, configServiceInstance } from 'common/config/provider';
import { ServiceAvailabilityCollectorService } from 'shared/services/service-availability-collector/service-availability-collector-service';
import { CompatibilityService } from 'common/compatibility/compatibility.service';
import { WindowSizeService } from 'common/window-size/window-size.service';
import { WorkItemPool } from 'common/workitems/workitem-pool';
import { GenesysCloudWorkItemNotificationsService } from 'shared/services/genesys-workitem-notifications/genesys-cloud-workitem-notifications.service';
import { PermissionDeniedModule } from 'components/permission-denied/permission-denied.module';
import { PermissionDeniedComponent } from 'components/permission-denied/permission-denied.component';
import { isDXProd } from 'environments/environment.helper';

const velocityjs = require('velocity-animate');

export class WebpackTranslateLoader implements TranslateLoader {
  getTranslation(lang: string): Observable<any> {
    return from(import(`./translations/${lang}.json`));
  }
}

const devOnlyModules = environment.production ? [] : [
  StoreDevtoolsModule.instrument({
    name: 'Maverick NgRx Store'
  })
];

const baseHrefFactory = (s: PlatformLocation) => config.isBoldDomain ? s.getBaseHrefFromDOM() : null;
const getCsrfTokenServiceType = () => config.isBoldDomain
  ? CsrfDoubleSubmitTokenService
  : CsrfSynchronizerTokenService;

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    TranslateModule.forRoot({
      defaultLanguage: isDXProd() ? 'en-US' : undefined,
      loader: {
        provide: TranslateLoader,
        useClass: WebpackTranslateLoader
      }
    }),
    NgbModule,
    Angulartics2RouterlessModule.forRoot(),
    ngfModule,
    CoolStorageModule.forRoot(),
    SvgIconModule,
    VirtualScrollerModule,
    FormsModule,
    NavbarModule,
    EditorModule,
    MatDialogModule,
    SvgAvatarModule,
    NotificationBarModule,
    LoginModule,
    SvgSymbolsModule,
    KeyboardShortcutsMenuModule,
    RootLayoutModule,
    SocketIoModule,
    VideoChatCallToastModule,
    StoreModule.forRoot({}),
    EffectsModule.forRoot([]),
    GenesysCloudStoreModule,
    TelemetryModule,
    ErrorHandlerModule,
    PermissionDeniedModule,
    ...devOnlyModules,
    RouterModule.forRoot([
      { path: bcStates.login.url, component: LoginComponent, canActivate: [LoginIdentityGuard] },
      {
        path: bcStates.logout.url, component: LoadingComponent, resolve: {
          logout: LogoutResolverService
        }
      },
      {
        path: bcStates.sso.url, component: LoadingComponent,
        resolve: {
          sso: SSOStateResolverService
        }
      },
      {
        path: bcStates.ssoError.url, component: LoadingComponent,
        resolve: {
          ssoError: SsoErrorResolverService
        }
      },
      { path: bcStates.permissionDenied.url, component: PermissionDeniedComponent },
      { path: '**', redirectTo: bcStates.layout.url }
    ], {
    initialNavigation: 'enabledBlocking',
    useHash: !config.isBoldDomain
})
  ],
  providers: [
    {
      provide: 'velocityjs',
      useValue: velocityjs
    },
    {
      provide: RxEmitter,
      useValue: RxEmitterInstance
    },
    {
      provide: APP_BASE_HREF,
      useFactory: baseHrefFactory,
      deps: [PlatformLocation]
    },
    { provide: HTTP_INTERCEPTORS,
      useClass: BcHttpInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: getCsrfTokenServiceType(),
      multi: true
    },
    {
      provide: CsrfTokenService,
      useClass: getCsrfTokenServiceType()
    },
    {
      provide: BcConfigService,
      useValue: configServiceInstance
    },
    {
      provide: WorkItemPool,
      useValue: WorkItemPool.getInstance()
    },
    CookieService, // Workaround for https://github.com/stevermeister/ngx-cookie-service/issues/100
    WorkItemsTrackingService,
    ChatsService,
    ChatsStoreFactoryService,
    TextChatsService,
    TextChatsStoreFactoryService,
    TextChatMessagesService,
    TextChatMessagesStoreFactoryService,
    TextChatUserService,
    TextChatUserStoreFactoryService,
    LaneManagerService,
    IntegrationsService,
    RemoteControlMessageService,
    WindowService,
    FileUploadService,
    UserPermissionService,
    ServiceEvaluatorService,
    DesktopPopupService,
    TranslationLoaderService,
    BrowserSupportService,
    PinviteList,
    EmailList,
    DiscussionList,
    ComposeEmailList,
    LoginSubscriber,
    LogoutSubscriber,
    ActiveAssistService,
    WorkAreaManager,
    WorkItemStateHandler,
    BrandingService,
    BcAnalytics,
    AudioService,
    AutoAnswersService,
    DynamicQueryAdapterService,
    DynamicQueryDecoratorService,
    TinyMCEPluginsService,
    MessageGroupService,
    WorkItemNotificationsService,
    GenesysCloudWorkItemNotificationsService,
    ActivityHandlerService,
    NotificationBarService,
    EmailAccountsService,
    EmailSignatureInjectorService,
    BcVisitorInfoBuilderService,
    WorkItemObservableCacheService,
    BackendFieldConverterService,
    StreamStatusService,
    BcSettingsService,
    BcRestApi,
    VisitorsService,
    ServiceAvailabilityCollectorService,
    CompatibilityService,
    WindowSizeService
  ],
  declarations: [
    CopyrightComponent,
    LiveChatComponent
  ],
  bootstrap: [LiveChatComponent]
})

export class BcLiveChatModule { }
